// src/api/dataWarehouseSnowflake.ts
//
// BYO-Snowflake warehouse client (ADR-0175 fase 2) — the Snowflake mirror of
// `dataWarehouse.ts`. Backend contract:
// `apps/backend/api/routes/account_dwh_snowflake_routes.py`, proxied by
// `apps/bff/src/routes/account-dwh-snowflake.routes.ts`.
//
// Two-phase flow, same shape as the BigQuery BYO branch:
//   1. POST /connect  — store address + credential, hand back the public key
//                       and a personalized grant script for the customer's DBA.
//   2. POST /validate — probe with the real credential; ACTIVE on success.

import { apiJson } from "./client";

export type SnowflakeDwhStatus = {
  /** "snowflake" once a Snowflake warehouse is linked; "bigquery"/null otherwise. */
  warehouse_kind: string | null;
  snowflake_account: string | null;
  snowflake_database: string | null;
  snowflake_warehouse: string | null;
  snowflake_role: string | null;
  snowflake_user: string | null;
  provisioning_status: string | null;
  error_message: string | null;
  public_key_b64: string | null;
  public_key_fingerprint: string | null;
};

/**
 * The connect POST is the ONLY source of `grant_script` — the status GET never
 * returns it. Re-showing the script therefore means re-POSTing connect, which
 * is idempotent and reuses the stored keypair (so a script the DBA already ran
 * stays valid).
 */
export type SnowflakeDwhConnectResult = SnowflakeDwhStatus & {
  grant_script: string | null;
};

export type SnowflakeDwhValidateResult = {
  provisioning_status: string;
  /** Customer-ready messages, each naming the exact missing GRANT. Render verbatim. */
  validation_errors: string[];
  region?: string | null;
};

/** The wire payload. Blank optionals must be OMITTED, never sent as "". */
export type SnowflakeDwhConnectPayload = {
  snowflake_account: string;
  snowflake_database: string;
  snowflake_warehouse?: string;
  snowflake_role?: string;
  snowflake_user?: string;
  private_key_pem?: string;
  regenerate_key?: boolean;
};

/** Backend defaults applied when the matching field is omitted. */
export const SNOWFLAKE_DEFAULTS = {
  warehouse: "VERITY_WH",
  role: "VERITY_RUNTIME",
  user: "VERITY_SVC",
} as const;

export type SnowflakeConnectFormValues = {
  account: string;
  database: string;
  warehouse: string;
  role: string;
  user: string;
  privateKeyPem: string;
};

/**
 * Trim everything and drop the blanks. A blank optional identifier must be
 * absent from the body — the backend fields are non-nullable strings with a
 * regex, so "" would fail validation instead of falling back to the default
 * (VERITY_WH / VERITY_RUNTIME / VERITY_SVC).
 */
export function buildSnowflakeConnectPayload(
  values: SnowflakeConnectFormValues,
  opts?: { regenerateKey?: boolean },
): SnowflakeDwhConnectPayload {
  const payload: SnowflakeDwhConnectPayload = {
    snowflake_account: values.account.trim(),
    snowflake_database: values.database.trim(),
  };

  const warehouse = values.warehouse.trim();
  if (warehouse) payload.snowflake_warehouse = warehouse;

  const role = values.role.trim();
  if (role) payload.snowflake_role = role;

  const user = values.user.trim();
  if (user) payload.snowflake_user = user;

  const key = values.privateKeyPem.trim();
  if (key) payload.private_key_pem = key;

  if (opts?.regenerateKey) payload.regenerate_key = true;

  return payload;
}

/** GET /accounts/dwh/snowflake */
export async function getSnowflakeDwhStatus(): Promise<SnowflakeDwhStatus> {
  return apiJson<SnowflakeDwhStatus>("/accounts/dwh/snowflake", {
    unwrapData: false,
  });
}

/** POST /accounts/dwh/snowflake/connect */
export async function connectSnowflakeDwh(
  payload: SnowflakeDwhConnectPayload,
): Promise<SnowflakeDwhConnectResult> {
  return apiJson<SnowflakeDwhConnectResult>("/accounts/dwh/snowflake/connect", {
    method: "POST",
    body: JSON.stringify(payload),
    unwrapData: false,
  });
}

/** POST /accounts/dwh/snowflake/validate */
export async function validateSnowflakeDwh(): Promise<SnowflakeDwhValidateResult> {
  return apiJson<SnowflakeDwhValidateResult>(
    "/accounts/dwh/snowflake/validate",
    { method: "POST", unwrapData: false },
  );
}
